import React from 'react';
import { Link } from 'react-router-dom';
import { useLegalContent } from '../hooks/useLegalContent';

const DynamicLegalContent: React.FC<{ documentType: 'terms_of_service' | 'privacy_policy' }> = ({ documentType }) => {
  const { content } = useLegalContent(documentType);
  if (!content || !content.sections || content.sections.length === 0) return null;

  return (
    <>
      <div className="mb-12">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
          {documentType === 'terms_of_service' ? 'Terms of Service' : 'Privacy Policy'}
        </h1>
        <p className="text-lg" style={{ color: 'var(--rp-text-secondary)' }}>
          Last Updated: {content.last_updated}
        </p>
      </div>
      <div className="space-y-8">
        {content.sections.map((section) => (
          <div
            key={section.id}
            className="p-6 rounded-lg border"
            style={{ borderColor: 'var(--rp-border)', backgroundColor: 'var(--rp-surface)' }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              {section.title}
            </h2>
            {section.content.split('\n\n').map((paragraph, i) => (
              paragraph.startsWith('\u2022') || paragraph.startsWith('- ') || paragraph.startsWith('* ') ? (
                <ul key={i} className="list-disc list-inside mb-4 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
                  {paragraph.split('\n').map((item, j) => (
                    <li key={j}>{item.replace(/^[\u2022\-*]\s*/, '')}</li>
                  ))}
                </ul>
              ) : (
                <p key={i} className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
                  {paragraph}
                </p>
              )
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

const PrivacyPolicy: React.FC = () => {
  const { content, loading } = useLegalContent('privacy_policy');
  const hasDbContent = !loading && content && content.sections && content.sections.length > 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rp-background)' }}>
      {/* Header */}
      <header className="border-b" style={{ borderColor: 'var(--rp-border)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/" className="text-2xl font-bold" style={{ color: 'var(--rp-primary)' }}>
            RidePulse
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* DB-driven content takes precedence */}
        {hasDbContent ? (
          <DynamicLegalContent documentType="privacy_policy" />
        ) : (
          <>
        {/* Hero Section */}
        <div className="mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
            Privacy Policy
          </h1>
          <p className="text-lg" style={{ color: 'var(--rp-text-secondary)' }}>
            Last Updated: October 2, 2026
          </p>
        </div>

        {/* Privacy Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              Introduction
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              Welcome to RidePulse! Your privacy is important to us. This Privacy Policy explains how Paulo
              Marin, doing business as RidePulse ("RidePulse," "we," "us," or "our"), collects, uses,
              shares, and protects your personal information when you use the RidePulse mobile application
              ("App") and related services ("Services"). By using RidePulse, you agree to the collection
              and use of information in accordance with this Privacy Policy. You must be at least 16 years
              old to use RidePulse.
            </p>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              Effective Date: October 2, 2026. Last Updated: October 2, 2026.
            </p>
          </div>

          {/* 1. Information We Collect */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              1. Information We Collect
            </h2>

            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>
              1.1 Information You Provide Directly
            </h3>
            <p className="mb-2" style={{ color: 'var(--rp-text-secondary)' }}>
              We collect information you provide directly when you create an account, set up your profile,
              manage your motorcycles, participate in events, or communicate with other users.
            </p>
            <p className="font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
              Account Information:
            </p>
            <ul className="list-disc list-inside mb-3 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Email address (required)</li>
              <li>Username and display name</li>
              <li>Password (encrypted)</li>
              <li>Profile photo (optional)</li>
              <li>Date of birth (to verify age 16+)</li>
            </ul>

            <p className="font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
              Profile Information:
            </p>
            <ul className="list-disc list-inside mb-3 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Riding experience level</li>
              <li>Riding style preferences</li>
              <li>Bio/description (optional)</li>
              <li>Emergency contact information (optional)</li>
            </ul>

            <p className="font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
              Motorcycle Information:
            </p>
            <ul className="list-disc list-inside mb-3 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Make, model, and year</li>
              <li>Motorcycle photos (optional)</li>
              <li>Specifications and modifications (optional)</li>
              <li>Primary motorcycle designation</li>
            </ul>

            <p className="font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
              Event Information:
            </p>
            <ul className="list-disc list-inside mb-3 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Event titles and descriptions</li>
              <li>Event locations and waypoints</li>
              <li>Event dates and times</li>
              <li>Participant lists</li>
              <li>Event photos and media</li>
            </ul>

            <p className="font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
              Communications:
            </p>
            <ul className="list-disc list-inside mb-3 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Messages sent through event chat</li>
              <li>Friend requests and connections</li>
              <li>Content reports and feedback</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>
              1.2 Information Collected Automatically
            </h3>
            <p className="mb-2" style={{ color: 'var(--rp-text-secondary)' }}>
              When you use RidePulse, certain information is collected automatically, including:
            </p>
            <ul className="list-disc list-inside mb-3 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>
                Location data when you browse events near you, create events, check in, or enable live
                tracking during events.
              </li>
              <li>Device information (device type, OS version, app version, device identifiers, language).</li>
              <li>Usage information (features used, events viewed/created/joined, time in app, searches).</li>
              <li>Technical information (IP address, crash logs, error reports, performance data).</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>
              1.3 Information from Third Parties
            </h3>
            <p className="mb-2" style={{ color: 'var(--rp-text-secondary)' }}>
              We also receive data from third-party services when you choose to connect them:
            </p>
            <ul className="list-disc list-inside space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>
                Sign in with Apple on iOS, or Google Sign-In on Android (identity, email, and name
                fields you choose to share).
              </li>
              <li>
                Apple services (iOS) such as MapKit / Apple Maps, WeatherKit, StoreKit, and Keychain to
                support mapping, weather, purchases, and secure credential storage.
              </li>
              <li>
                Google services such as Google Maps Platform (Android, and optional on iOS), Google
                Play Billing, and Firebase Cloud Messaging. Google Maps features are subject to the
                Google Maps/Google Earth Additional Terms of Service and the Google Privacy Policy.
              </li>
            </ul>
          </div>

          {/* 2. How We Use Your Information */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              2. How We Use Your Information
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              We use your information to operate, secure, and improve RidePulse:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Create and manage your account and profile.</li>
              <li>Enable you to create, discover, and join events.</li>
              <li>Connect you with other riders and display your garage.</li>
              <li>Facilitate event chat, messaging, and community features.</li>
              <li>Process premium subscriptions and provide customer support.</li>
              <li>Verify age requirements and enforce safety and security.</li>
              <li>
                Provide location-based features such as nearby events, navigation, check-ins, and live
                tracking.
              </li>
              <li>Personalize recommendations and app experience.</li>
              <li>Analyze usage, fix bugs, and develop new features.</li>
            </ul>
          </div>

          {/* 3. How We Share Your Information */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              3. How We Share Your Information
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              We do not sell your personal information. We share your data in limited circumstances:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>With other users via your public profile and event participation.</li>
              <li>With service providers like Supabase and Resend to operate the platform.</li>
              <li>
                With Apple and Google services to provide maps, weather, authentication, billing, and
                notifications.
              </li>
              <li>
                When required by law or to protect our rights, users, and the public, including fraud and
                abuse prevention.
              </li>
              <li>As part of a business transfer, merger, or acquisition, with notice to you.</li>
              <li>With your explicit consent for other specific purposes.</li>
            </ul>
          </div>

          {/* 4. Your Privacy Rights */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              4. Your Privacy Rights
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              You have rights regarding how your personal information is used, including access, correction,
              deletion, and portability rights, as well as additional protections under CCPA and GDPR-style
              frameworks.
            </p>
            <ul className="list-disc list-inside space-y-1 mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Request a copy of your data within 72 hours by emailing support@ridepulse.app.</li>
              <li>Update your profile, garage, privacy, and emergency contact settings in the app.</li>
              <li>
                Delete your account in the app (Settings &gt; Account &gt; Delete Account), via an
                &quot;Account or data deletion&quot; request on our <Link to="/contact" className="underline">contact page</Link>,
                or by emailing support@ridepulse.app. Web and email requests are verified against the
                account email before processing. Data is soft-deleted for 30 days and then permanently
                removed.
              </li>
              <li>
                Control location tracking, notifications, and communications through device and in-app
                settings.
              </li>
              <li>
                Exercise additional rights if you are a California resident or subject to GDPR-level
                protections.
              </li>
            </ul>
          </div>

          {/* 5. Data Retention */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              5. Data Retention
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              We retain your information for as long as necessary to provide services and comply with legal
              obligations. Active accounts are retained while your account is in use. If you delete your
              account, your personal information is kept for 30 days (soft delete) and then permanently
              deleted. Event data may be retained and anonymized to preserve event history. Backups are kept
              for disaster recovery and then deleted after a limited period.
            </p>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              GroupPulse data: while sharing, only your latest position is stored - no location history is
              kept. After an event has been completed or cancelled for 30 days, its GroupPulse tracking
              records, sessions, and messages become eligible for removal and are deleted by the next
              scheduled weekly cleanup.
            </p>
            <p className="mb-2 font-semibold" style={{ color: 'var(--rp-text-secondary)' }}>
              Premium Subscription Provider Events:
            </p>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              When you purchase or renew a Premium subscription through Apple App Store or Google Play,
              we retain anonymized provider event records (purchase confirmations, renewal notifications,
              refund and revocation events) for up to 180 days after the associated subscription period
              ends. These records are used for entitlement verification, fraud prevention, refund dispute
              resolution, and financial reconciliation. After 180 days, these records are automatically
              deleted. We do not retain your full payment card numbers or bank account details — payment
              processing is handled entirely by Apple and Google, and we only receive subscription status
              and transaction identifiers from them.
            </p>
          </div>

          {/* 5a. GroupPulse Live Location Sharing */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              5a. GroupPulse Live Location Sharing
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              GroupPulse is a Premium feature that shares your real-time location with participants of the
              same event while a session is active. It is opt-in: sharing begins only when you start it,
              after granting location permission (and on Android, acknowledging the prominent disclosure
              and granting background location).
            </p>
            <ul className="list-disc list-inside mb-4 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>
                Your current GPS position is shared with participants of the same GroupPulse session who
                are not blocked, and updated periodically while the session runs.
              </li>
              <li>
                Sharing continues when the app is not on screen - via iOS background location updates or
                Android&apos;s foreground service with a persistent notification.
              </li>
              <li>
                We store your latest position as a single tracking record that is updated in place; we do
                not keep a history of your movements.
              </li>
            </ul>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              When sharing stops - when you leave the session, the event ends, you sign out, or you revoke
              location permission - your live position stops updating and is no longer shown to other
              participants. On Android, reopening an in-progress event you were sharing in may resume your
              session if location permission is still granted; you can stop again at any time. Your tracking
              record remains attached to the event for history; once the event has been completed or
              cancelled for 30 days, the record becomes eligible for removal and is deleted by the next
              scheduled weekly cleanup.
            </p>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              If you leave early with location visibility restricted, no departure coordinates are stored.
            </p>
            <p className="mb-2 font-semibold" style={{ color: 'var(--rp-text-secondary)' }}>
              Your controls:
            </p>
            <ul className="list-disc list-inside mb-4 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>You can leave a GroupPulse session at any time to stop sharing your location.</li>
              <li>You can disable location permissions for RidePulse in your device settings at any time.</li>
              <li>GroupPulse requires an active Premium subscription to share your location.</li>
              <li>
                The event host can start and end GroupPulse sessions for their event, but cannot access
                your location after the session ends.
              </li>
            </ul>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              GroupPulse location data is processed to provide real-time participant tracking, proximity
              alerts, and arrival detection. It is not used for advertising or sold to third parties.
            </p>
          </div>

          {/* 5b. Premium Subscriptions and Payment Processing */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              5b. Premium Subscriptions and Payment Processing
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              RidePulse Premium is a subscription service billed through Apple App Store or Google Play
              Store. We use Apple's and Google's in-app purchase systems for all payment processing.
            </p>
            <p className="mb-2 font-semibold" style={{ color: 'var(--rp-text-secondary)' }}>
              What we collect and store:
            </p>
            <ul className="list-disc list-inside mb-4 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Subscription status (active, expired, revoked, in grace period)</li>
              <li>Subscription platform (Apple or Google)</li>
              <li>Subscription period start and end dates</li>
              <li>Auto-renewal status</li>
              <li>Transaction and original-transaction identifiers from Apple/Google</li>
              <li>Server-to-server notifications from Apple and Google about subscription lifecycle events (renewals, cancellations, refunds, revocations)</li>
            </ul>
            <p className="mb-2 font-semibold" style={{ color: 'var(--rp-text-secondary)' }}>
              What we do NOT collect or store:
            </p>
            <ul className="list-disc list-inside mb-4 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Full credit card numbers or bank account details</li>
              <li>Billing addresses (managed by Apple/Google)</li>
              <li>Payment method tokens (managed by Apple/Google)</li>
            </ul>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              All payment processing is handled by Apple and Google. We receive only subscription status
              and transaction identifiers from them, never your full payment credentials. Subscription
              validation is performed server-side using Apple's App Store Server API and Google Play
              Developer API to verify purchases directly with the stores, ensuring your entitlement is
              always accurate without requiring you to restore purchases manually.
            </p>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              You can manage or cancel your Premium subscription at any time through your Apple ID
              settings (for App Store purchases) or Google Play Store settings (for Play Store
              purchases). Cancelling stops auto-renewal but does not immediately revoke access — you
              retain Premium access until the current billing period ends.
            </p>
          </div>

          {/* 6. Data Security */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              6. Data Security
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              We use industry-standard measures to protect your data, including encryption in transit and at
              rest, secure password hashing, access controls, audit logging, and Supabase&apos;s managed
              infrastructure protections. However, no system is completely secure. You are responsible for
              keeping your password safe, not sharing your account, and using a secure device.
            </p>
          </div>

          {/* 7. Children's Privacy */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              7. Children&apos;s Privacy
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              RidePulse is not intended for users under 16 years old. We do not knowingly collect information
              from children under 16. If we learn that we have collected such information, we will delete it
              promptly. If you believe a child under 16 has provided information to RidePulse, please contact
              us at support@ridepulse.app.
            </p>
          </div>

          {/* 8. International Data Transfers */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              8. International Data Transfers
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              RidePulse is initially launching in the United States and Canada. Your information is stored on servers
              located in US-based Supabase data centers. If you access RidePulse from outside the United
              States or Canada, you consent to the transfer and processing of your information in the
              United States.
            </p>
          </div>

          {/* 9. Third-Party Links and Services */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              9. Third-Party Links and Services
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              RidePulse may link to third-party websites or integrate with external services such as Apple
              Maps / MapKit, WeatherKit, Sign in with Apple, and StoreKit (iOS); Google Maps Platform,
              Google Sign-In, Google Play Billing, and Firebase Cloud Messaging; Supabase; and Resend.
              Events may include optional external links provided by the organizer (for example, a
              Facebook event page). We are not responsible for the privacy practices, content, or data
              collection of these third parties. Each service has its own privacy policy, which we
              encourage you to review.
            </p>
          </div>

          {/* 10. Content Moderation and Safety */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              10. Content Moderation and Safety
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              Event hosts have moderation tools for their event chats, and automated systems help prevent
              abuse and inappropriate content. Users can report content or other users. We review reported
              content, prioritize illegal or harmful material, and may remove content or suspend accounts as
              needed to protect the community.
            </p>
          </div>

          {/* 11. Changes to This Privacy Policy */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              11. Changes to This Privacy Policy
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              We may update this Privacy Policy from time to time. When we make material changes, we will
              update the "Last Updated" date and may notify you via in-app notifications, email, or other
              prominent notices. Continued use of RidePulse after changes become effective means you accept
              the updated policy.
            </p>
          </div>

          {/* 12. Contact Us */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              12. Contact Us
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              If you have questions, concerns, or requests regarding this Privacy Policy or your personal
              information, contact us at:
            </p>
            <p className="mb-2 font-medium" style={{ color: 'var(--rp-primary)' }}>
              support@ridepulse.app
            </p>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              We aim to respond to privacy-related requests within 72 hours, general inquiries within 5
              business days, and urgent safety issues as quickly as possible.
            </p>
          </div>

          {/* 13. Your Consent */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              13. Your Consent
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              By using RidePulse, you consent to this Privacy Policy, to the collection and use of your
              information as described, to the transfer of your information to the United States, and to
              processing by our service providers. You can withdraw consent at any time by adjusting your
              privacy settings, deleting your account, or contacting support@ridepulse.app.
            </p>
          </div>
        </div>
        </>)}

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t" style={{ borderColor: 'var(--rp-border)' }}>
          <div className="flex flex-wrap gap-6 justify-center">
            <Link 
              to="/" 
              className="hover:underline"
              style={{ color: 'var(--rp-text-secondary)' }}
            >
              Home
            </Link>
            <Link 
              to="/legal/terms" 
              className="hover:underline"
              style={{ color: 'var(--rp-text-secondary)' }}
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
